import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { RoofingMaterials, roofingMaterials } from "@/components/RoofingMaterials";
import heroImg from "@/assets/mckay/roofing-service.webp.asset.json";
import proj1 from "@/assets/blog/blog-1.jpg.asset.json";
import proj2 from "@/assets/blog/blog-2.jpg.asset.json";
import proj3 from "@/assets/blog/blog-3.jpg.asset.json";

const PAGE_URL = "https://mckay.steadyautogrowthdemo.com/roofing-materials";

export const Route = createFileRoute("/roofing-materials")({
  head: () => ({
    meta: [
      { title: "Types of Roofing Materials We Install | McKay Roofing & Solar" },
      {
        name: "description",
        content:
          "Asphalt and luxury shingles, concrete and clay tile, PVC and TPO, standing seam metal, architectural sheet metal, hot mop and torch-down. See the roofing materials McKay installs across Southern California.",
      },
      {
        name: "keywords",
        content:
          "types of roofing materials, luxury shingles, concrete tile roof, clay tile roof, PVC roofing, TPO roofing, standing seam metal roof, architectural sheet metal, hot mop roofing, torch down roofing Southern California",
      },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Types of Roofing Materials We Install | McKay Roofing & Solar" },
      {
        property: "og:description",
        content:
          "Shingle, tile, single-ply, metal and built-up systems installed across Southern California, with three recent projects that show the work.",
      },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImg.url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Types of Roofing Materials We Install | McKay Roofing & Solar" },
      {
        name: "twitter:description",
        content: "From luxury shingles to torch-down flat roof systems - what we install and when we recommend it.",
      },
      { name: "twitter:image", content: heroImg.url },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
  }),
  component: RoofingMaterialsPage,
});

type Project = {
  title: string;
  location: string;
  material: string;
  body: string;
  image: string;
  alt: string;
  bullets: string[];
};

const projects: Project[] = [
  {
    title: "Gravel tear-off to a new 60 mil TPO roof, then solar",
    location: "Southern California",
    material: "PVC & TPO",
    body: "This building carried an old gravel built-up roof that had been patched more times than anyone could count. We stripped it to clean decking, dried it in, and heat-welded a new 60 mil TPO system with fabricated metal at every edge and scupper. Once the membrane passed inspection the solar array went on top of a roof with decades left in it.",
    image: proj1.url,
    alt: "New white TPO membrane roof with welded seams and fresh edge metal on a Southern California building",
    bullets: [
      "Full gravel tear-off with decking repaired before the membrane went down",
      "Heat-welded seams and custom sheet metal at edges, curbs and scuppers",
      "Reflective surface that pulled attic and interior temperatures down",
      "Solar sequenced after the roof so no panels were set on tired material",
    ],
  },
  {
    title: "Cap sheet removal and a new torch-down flat roof",
    location: "Southern California",
    material: "Hot & Torch",
    body: "A tired cap sheet was holding water in two low spots and the seams were opening up. We took it down to bare deck, corrected the slope toward the drains, and torched down a new modified bitumen system in multiple plies. It came with a 20-year manufacturer warranty, and it is still the toughest answer for a flat roof that sees foot traffic.",
    image: proj2.url,
    alt: "Crew installing a new torch-down modified bitumen flat roof system",
    bullets: [
      "Old cap sheet removed down to clean, dry decking",
      "Ponding corrected with new slope and reworked drain details",
      "Multi-ply torch-applied modified bitumen backed by a 20-year warranty",
      "Walk pads set where service techs actually cross the roof",
    ],
  },
  {
    title: "Ocean Beach flat roof replacement on Bacon Street",
    location: "Ocean Beach, Southern California",
    material: "Architectural sheet metal + torch-down",
    body: "Salt air and coastal fog a few blocks from the sand are hard on any roof, and this one had run out of life. We replaced the system end to end and hand-bent new coping, gravel stops and scupper boxes on site so the metal actually fit the parapet instead of being forced into place. Water leaves the roof now instead of sitting on it.",
    image: proj3.url,
    alt: "Coastal flat roof replacement with new custom bent sheet metal coping in Ocean Beach",
    bullets: [
      "Custom-fabricated coping, gravel stops and scupper boxes bent to the parapet",
      "Coastal-grade fasteners and sealants chosen for salt exposure",
      "Drainage reworked so the low corners stopped holding water",
      "Neighbors' driveways and landscaping protected through the whole tear-off",
    ],
  },
];

function RoofingMaterialsPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Roofing material installation",
    serviceType: roofingMaterials.map((m) => m.name),
    provider: {
      "@type": "RoofingContractor",
      name: "McKay Roofing & Solar",
      telephone: "+1-858-541-1097",
      address: {
        "@type": "PostalAddress",
        streetAddress: "9520 Padgett St, Suite 107",
        addressLocality: "San Diego",
        addressRegion: "CA",
        postalCode: "92126",
        addressCountry: "US",
      },
    },
    areaServed: { "@type": "AdministrativeArea", name: "Southern California" },
    url: PAGE_URL,
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <section className="relative isolate overflow-hidden">
          <HeroBackground src={heroImg.url} alt="Roof under construction showing new roofing material being installed" />
          <div className="relative z-10 mx-auto flex min-h-[70vh] max-w-7xl flex-col justify-center px-4 py-24 sm:px-6 lg:px-8">
            <div className="max-w-3xl text-primary-foreground">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">Roofing materials</div>
              <h1 className="mt-4 font-serif text-4xl font-bold sm:text-6xl text-balance">
                Types of Roofing Materials
              </h1>
              <p className="mt-5 text-lg text-white/90">
                McKay Roofing &amp; Solar carries the roofing materials that answer both sides of the job - keeping the
                house dry and looking right from the curb. Our specialists assess each roof with the homeowner before
                anyone picks a product.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="btn-glow btn-lift bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  <Link to="/contact">
                    Get Free Quote <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" className="btn-lift bg-accent text-accent-foreground hover:bg-accent/90">
                  <Link to="/contact">(858) 541-1097</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <RoofingMaterials showCta={false} heading="Every material we install" />

        {/* Featured projects */}
        <section className="relative overflow-hidden bg-background">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">Featured projects</div>
              <h2 className="mt-3 font-serif text-4xl font-bold text-primary sm:text-5xl text-balance">
                Three projects that show the materials at work
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Photos and product names only go so far. Here is how these systems went together on real Southern
                California roofs.
              </p>
            </div>

            <div className="mt-14 flex flex-col gap-16 sm:gap-20">
              {projects.map((p, i) => (
                <div
                  key={p.title}
                  className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                >
                  <div className="overflow-hidden rounded-3xl border border-border shadow-elegant">
                    <img
                      src={p.image}
                      alt={p.alt}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                    />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-[0.16em]">
                      <span className="rounded-full bg-secondary/10 px-3 py-1 text-secondary">{p.material}</span>
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" /> {p.location}
                      </span>
                    </div>
                    <h3 className="mt-4 font-serif text-3xl font-bold text-primary sm:text-4xl text-balance">{p.title}</h3>
                    <p className="mt-4 leading-relaxed text-muted-foreground">{p.body}</p>
                    <ul className="mt-5 space-y-2.5">
                      {p.bullets.map((b) => (
                        <li key={b} className="flex gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap gap-3">
                      <Button asChild size="lg" className="btn-glow btn-lift bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                        <Link to="/contact">
                          Get Free Quote <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild size="lg" variant="outline" className="btn-lift">
                        <Link to="/gallery">See more projects</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </div>
  );
}
